"use client";

import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useIssues } from "@/lib/hooks/useIssues";
import { Issue, IssueStatus, IssuePriority, ISSUE_PRIORITIES, PRIORITY_COLORS } from "@/types/issue";
import { Plus, Search, Check, ChevronDown, Upload, X, Image as ImageIcon } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { useAuth } from "@/lib/hooks/useAuth";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/components/ui/use-toast";
import { useNotifications } from "@/lib/contexts/NotificationContext";

interface CreateIssueProps {
  children?: React.ReactNode;
}

export function CreateIssue({ children }: CreateIssueProps) {
  const { user } = useAuth();
  const { createIssue, getAvailableUsers } = useIssues();
  const { addNotification } = useNotifications();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState<IssuePriority>("Medium");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [users, setUsers] = useState<any[]>([]);
  const [assigneeOpen, setAssigneeOpen] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);

  useEffect(() => {
    const loadUsers = async () => {
      try {
        setLoading(true);
        const availableUsers = await getAvailableUsers();
        setUsers(availableUsers);
      } catch (error) {
        console.error('Error loading users:', error);
      } finally {
        setLoading(false);
      }
    };

    if (assigneeOpen) {
      loadUsers();
    }
  }, [assigneeOpen, getAvailableUsers]);

  const filteredUsers = users.filter((user) => {
    if (!search) return true;
    const searchTerm = search.toLowerCase();
    const displayName = (user.display || "").toLowerCase();
    const email = (user.email || "").toLowerCase();
    return displayName.includes(searchTerm) || email.includes(searchTerm);
  });

  const handleUserSelect = (selectedUser: any) => {
    setSelectedUsers(prev => {
      const isSelected = prev.some(user => user.id === selectedUser.id);
      if (isSelected) {
        return prev.filter(user => user.id !== selectedUser.id);
      } else {
        return [...prev, selectedUser];
      }
    });
  };

  const removeUser = (userToRemove: any) => {
    setSelectedUsers(prev => prev.filter(user => user.id !== userToRemove.id));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    
    if (imageFiles.length > 0) {
      setAttachments(prev => [...prev, ...imageFiles]);
      
      // Create preview URLs for the new images
      const newPreviewUrls = imageFiles.map(file => URL.createObjectURL(file));
      setPreviewUrls(prev => [...prev, ...newPreviewUrls]);
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
    
    // Revoke the URL to prevent memory leaks
    URL.revokeObjectURL(previewUrls[index]);
    setPreviewUrls(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!title.trim() || !description.trim() || !user || selectedUsers.length === 0) return;

    setIsSubmitting(true);
    try {
      const now = new Date().toISOString();
      const newIssue = {
        title: title.trim(),
        description: description.trim(),
        status: "New" as IssueStatus,
        priority,
        createdBy: {
          id: user.uid,
          name: user.displayName || 'Unknown User',
          email: user.email || '',
          photoURL: user.photoURL || null,
        },
        assignedTo: selectedUsers.map(selectedUser => ({
          id: selectedUser.id,
          name: selectedUser.display,
          email: selectedUser.email || '',
          photoURL: selectedUser.photoURL || null,
        })),
        createdAt: now,
        updatedAt: now,
        updatedBy: {
          id: user.uid,
          name: user.displayName || 'Unknown User',
          email: user.email || '',
          photoURL: user.photoURL || null,
        },
        comments: [],
        activity: [{
          id: crypto.randomUUID(),
          type: 'update',
          content: 'Issue created',
          createdAt: now,
          createdBy: {
            id: user.uid,
            name: user.displayName || 'Unknown User',
            email: user.email || '',
            photoURL: user.photoURL || null,
          }
        }],
        attachments: [], // This will be populated by createIssue
      };

      // Pass both the issue data and attachments to createIssue
      const createdIssue = await createIssue(newIssue, attachments);

      // Send notifications to all assigned users
      selectedUsers.forEach(assignedUser => {
        addNotification({
          type: 'issue',
          title: 'New Assignment',
          message: `You have been assigned to "${title.trim()}"`,
          userId: assignedUser.id,
          link: `/issues/${createdIssue.id}`
        });
      });
      
      // Reset form and close dialog
      setTitle("");
      setDescription("");
      setPriority("Medium");
      setSelectedUsers([]);
      setAttachments([]);
      setPreviewUrls([]);
      setOpen(false);

      // Show success toast
      toast({
        title: "Success",
        description: "Issue created successfully",
      });
    } catch (error) {
      console.error('Error creating issue:', error);
      toast({
        title: "Error",
        description: "Failed to create issue. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Cleanup preview URLs when dialog closes
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      previewUrls.forEach(url => URL.revokeObjectURL(url));
      setPreviewUrls([]);
      setAttachments([]);
    }
    setOpen(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {children || (
          <Button>
            <Plus className="h-4 w-4 mr-2" />
            Create Issue
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Create New Issue</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="space-y-4">
            <div>
              <Label>Title</Label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Issue title..."
                className="mt-1.5"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe the issue..."
                className="mt-1.5 min-h-[100px]"
              />
            </div>
            <div>
              <Label>Priority</Label>
              <Select value={priority} onValueChange={(value: IssuePriority) => setPriority(value)}>
                <SelectTrigger className="mt-1.5">
                  <SelectValue>
                    <div className="flex items-center gap-2">
                      <Badge className={cn("text-xs", PRIORITY_COLORS[priority])}>
                        {priority}
                      </Badge>
                    </div>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {ISSUE_PRIORITIES.map((p) => (
                    <SelectItem key={p} value={p}>
                      <div className="flex items-center gap-2">
                        <Badge className={cn("text-xs", PRIORITY_COLORS[p])}>
                          {p}
                        </Badge>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Assigned to</Label>
              <Popover modal={true} open={assigneeOpen} onOpenChange={setAssigneeOpen}>
                <PopoverTrigger asChild>
                  <div className="relative mt-1.5">
                    <Input
                      placeholder="Search and select assignees..."
                      value={search}
                      onClick={() => setAssigneeOpen(true)}
                      onChange={(e) => setSearch(e.target.value)}
                      className={cn(
                        "w-full pr-9",
                        selectedUsers.length > 0 && "pl-8"
                      )}
                    />
                    {selectedUsers.length > 0 && (
                      <div className="absolute left-2 top-1/2 -translate-y-1/2 flex items-center">
                        <div className="relative">
                          <Avatar className="h-5 w-5 border border-border">
                            <AvatarImage src={selectedUsers[0].photoURL} />
                            <AvatarFallback className="text-xs">
                              {selectedUsers[0].display?.[0] || '?'}
                            </AvatarFallback>
                          </Avatar>
                          {selectedUsers.length > 1 && (
                            <div className="absolute -right-1 -top-1 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-primary text-[10px] leading-none text-primary-foreground">
                              +{selectedUsers.length - 1}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    <ChevronDown className="absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 opacity-50" />
                  </div>
                </PopoverTrigger>
                <PopoverContent 
                  className="w-[300px] p-0 z-50" 
                  align="start" 
                  side="bottom"
                  sideOffset={4}
                  onEscapeKeyDown={() => setAssigneeOpen(false)}
                  onInteractOutside={(e) => {
                    e.preventDefault();
                  }}
                >
                  <div className="rounded-lg border shadow-md bg-popover">
                    <div className="relative h-[200px] overflow-hidden bg-background">
                      <ScrollArea 
                        className="h-full overflow-y-auto" 
                        type="always"
                        scrollHideDelay={0}
                      >
                        <div 
                          className="p-1.5 pr-4"
                          onWheel={(e) => {
                            e.stopPropagation();
                            const scrollArea = e.currentTarget.parentElement;
                            if (scrollArea) {
                              scrollArea.scrollTop += e.deltaY;
                            }
                          }}
                        >
                          {loading ? (
                            <div className="py-6 text-center text-sm text-muted-foreground">
                              Loading users...
                            </div>
                          ) : filteredUsers.length === 0 ? (
                            <div className="py-6 text-center text-sm text-muted-foreground">
                              No users found
                            </div>
                          ) : (
                            <div className="space-y-1">
                              {filteredUsers.map((user) => {
                                const isSelected = selectedUsers.some(selected => selected.id === user.id);
                                return (
                                  <div
                                    key={user.id}
                                    role="button"
                                    tabIndex={0}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleUserSelect(user);
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        handleUserSelect(user);
                                      }
                                    }}
                                    className={cn(
                                      "flex items-center gap-2 px-2 py-1.5 rounded-md w-full text-left cursor-pointer select-none",
                                      "transition-colors hover:bg-accent hover:text-accent-foreground",
                                      isSelected ? "bg-primary/10 text-primary" : ""
                                    )}
                                  >
                                    <Avatar className="h-8 w-8 border bg-background">
                                      <AvatarImage src={user.photoURL} />
                                      <AvatarFallback className="bg-primary/10 text-primary">
                                        {user.display?.[0] || '?'}
                                      </AvatarFallback>
                                    </Avatar>
                                    <div className="flex flex-col flex-1 min-w-0">
                                      <span className="text-sm font-medium truncate">
                                        {user.display}
                                      </span>
                                      {user.email && (
                                        <span className="text-xs text-muted-foreground truncate">
                                          {user.email}
                                        </span>
                                      )}
                                    </div>
                                    {isSelected && (
                                      <Check className="h-4 w-4 flex-shrink-0 text-primary" />
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </ScrollArea>
                    </div>
                    <div className="p-3 border-t bg-muted">
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="flex -space-x-2">
                            {selectedUsers.map((user, index) => (
                              <Avatar 
                                key={user.id}
                                className="h-6 w-6 border-2 border-muted"
                                style={{ zIndex: selectedUsers.length - index }}
                              >
                                <AvatarImage src={user.photoURL} />
                                <AvatarFallback className="bg-primary/10 text-primary text-xs">
                                  {user.display?.[0] || '?'}
                                </AvatarFallback>
                              </Avatar>
                            ))}
                          </div>
                          <span className="text-sm text-muted-foreground truncate">
                            {selectedUsers.length} selected
                          </span>
                        </div>
                        <Button 
                          size="sm" 
                          variant="secondary"
                          onClick={() => {
                            setAssigneeOpen(false);
                            setSearch("");
                          }}
                          className="shrink-0"
                        >
                          Done
                        </Button>
                      </div>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium">
                Attachments ({previewUrls.length})
              </label>
              <ScrollArea className="h-[120px] border rounded-md p-1">
                <div className="grid grid-cols-4 gap-1 pr-3">
                  {previewUrls.map((url, index) => (
                    <div key={index} className="relative group aspect-square">
                      <img
                        src={url}
                        alt={`Attachment ${index + 1}`}
                        className="w-full h-full object-cover rounded-sm border"
                      />
                      <button
                        type="button"
                        onClick={() => removeAttachment(index)}
                        className="absolute top-0.5 right-0.5 p-0.5 bg-background/80 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="h-2.5 w-2.5" />
                      </button>
                    </div>
                  ))}
                  <label className="flex flex-col items-center justify-center aspect-square border border-dashed rounded-sm cursor-pointer hover:border-primary/50 transition-colors">
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={handleFileChange}
                    />
                    <ImageIcon className="h-4 w-4 mb-0.5 text-muted-foreground" />
                    <span className="text-[10px] text-muted-foreground">Add</span>
                  </label>
                </div>
              </ScrollArea>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setOpen(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting || !title.trim() || !description.trim() || selectedUsers.length === 0}
              >
                {isSubmitting ? "Creating..." : "Create Issue"}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
} 